import { useState, useEffect, useCallback, useRef } from "react";
import { Select, Divider, notification, DatePicker } from "antd";
import {
  ssidValidation,
  uuidValidation,
  macValidation,
  lengthValidation,
  typeValidation
} from "../../utils/formValidation";
import { getBoardsByPED } from "../../api/getBoardsByPED";
import service from "../../services/Service";
import { useGetVersionsLookupQuery } from '../../services/lookupApi.js';
import dayjs from 'dayjs';
import { withLocale } from "../../l10n";
import Loading from "../../utils/loading";
import withNavigation from '../hooks/withNavigation';
import "./KiwiCard.scss";

const { Option } = Select;

function KiwiCard({ lang, navigate, ident, location, editable }) {
  // ===== 1. ESTADOS =====
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    uuid: "",
    order_id: "",
    serial_number: "",
    work_order: "",
    ssid: "",
    mac: "",
    board_uuid: "",
    date: dayjs().format('DD-MM-YYYY'),
    version_uuid: "",
    notes: "",
    state: "Asignado",
  });

  const [errors, setErrors] = useState({
    ssid: "",
    mac: "",
    serial_number: "",
    work_order: "",
    board_uuid: "",
  });

  const [boardItems, setBoardItems] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [workOrdersOriginalLength, setWorkOrdersOriginalLength] = useState(0);

  const timeoutRef = useRef(null);

  // ===== 2. SELECTORES =====
  const { data: versions = [] } = useGetVersionsLookupQuery();

  // ===== 3. HELPERS =====

  const updateForm = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const updateError = useCallback((field, errorMessage) => {
    setErrors(prev => ({ ...prev, [field]: errorMessage }));
  }, []);

  const isFormValid = useCallback(() => {
    return (
      form.order_id.trim() !== "" &&
      form.ssid.trim() !== "" &&
      form.date.trim() !== "" &&
      form.state.trim() !== "" &&
      form.version_uuid.trim() !== "" &&
      !errors.ssid &&
      !errors.mac &&
      !errors.serial_number &&
      !errors.work_order &&
      !errors.board_uuid
    );
  }, [form, errors]);

  // ===== 4. FUNCIONES PRINCIPALES =====

  const fetchKiwiData = useCallback(async () => {
    if (!ident) return;

    try {
      setLoading(true);
      const res = await service.get(`kiwi/${ident}`);

      if (!res.data.ok) {
        notification.error({
          message: lang.handle(res.data.message),
        });
        return;
      }

      const dataFetched = res.data.data;
      const safeDate = dataFetched.date 
        ? dayjs(dataFetched.date).format("DD-MM-YYYY") 
        : dayjs().format("DD-MM-YYYY");

      const shouldAssign = location.pathname.includes("assing");

      setForm({
        uuid: dataFetched.uuid ?? "",
        order_id: dataFetched.order_id ?? "",
        serial_number: dataFetched.serial_number ?? "",
        work_order: dataFetched.work_order ?? "",
        ssid: dataFetched.ssid ?? "",
        mac: dataFetched.mac ?? "",
        board_uuid: dataFetched.board_uuid ?? "",
        date: safeDate,
        version_uuid: dataFetched.version_uuid ?? "",
        state: shouldAssign ? "Asignado" : dataFetched.state,
        notes: dataFetched.notes ?? "",
      });

      const result = await getBoardsByPED(dataFetched.order_id);
      setBoardItems(Array.isArray(result) ? [...new Set(result)] : []);

    } catch (err) {
      console.error("Error fetching kiwi data:", err);
      notification.error({
        message: lang.handle('Error al obtener datos'),
      });
    } finally {
      setLoading(false);
    }
  }, [ident, lang, location.pathname]);

  const debouncedFetchBoards = useCallback((value) => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(async () => {
      try {
        const result = await getBoardsByPED(value || "");
        setBoardItems(Array.isArray(result) ? [...new Set(result)] : []);
      } catch (err) {
        console.error("Error fetching boards:", err);
        notification.error({ message: lang.handle('Error al obtener datos') });
      }
    }, 300);
  }, [lang]);

  const fetchWorkOrders = useCallback(async () => {
    try {
      const res = await service.get('orders/wo', { pending: true });
      if (res && res.data.ok) {
        setWorkOrders(
          res.data.data.map((item) => ({
            label: item.wo,
            value: item.wo,
            serialNumber: item.serialNumber,
          }))
        );
        setWorkOrdersOriginalLength(res.data.data.length);
      }
    } catch (err) {
      console.error('Error al obtener ordenes de trabajo pendientes:', err);
    }
  }, []);

  // ===== 5. HANDLERS =====
  const onInputChange = useCallback((e) => {
    const { name, value } = e.target;

    switch (name) {
      case "ssid":
        updateError("ssid", ssidValidation(value) ? "" : lang.handle('¡SSID erróneo!'));
        break;
      case "mac":
        updateError("mac", macValidation(value) ? "" : lang.handle('¡MAC errónea!'));
        break;
      case "serial_number":
        updateError(
          "serial_number",
          lengthValidation(value, 14) ? "" : lang.handle('¡Número de serie erróneo!')
        );
        break;
      case "work_order":
        updateError(
          "work_order",
          lengthValidation(value, 8) && typeValidation(value, 'number')
            ? ""
            : lang.handle('¡Orden de trabajo erronea!')
        );
        break;
      case "board_uuid":
        updateError("board_uuid", uuidValidation(value) ? "" : lang.handle('¡UUID erróneo!'));
        break;
      case "order_id":
        debouncedFetchBoards(value);
        break;
      default:
        break;
    }

    updateForm(name, value);
  }, [lang, updateForm, updateError, debouncedFetchBoards]);

  const handleSearchWorkOrder = useCallback((value) => {
    setWorkOrders((prev) => {
      const list = [...prev];
      if (workOrdersOriginalLength !== list.length) {
        list.pop();
      }
      if (!list.find((e) => e.value.toString().indexOf(value) >= 0) && value) {
        list.push({ label: value, value: value });
      }
      return list;
    });
  }, [workOrdersOriginalLength]);

  const handleChangeWorkOrder = useCallback((value) => {
    updateError(
      "work_order",
      lengthValidation(value, 8) && typeValidation(value, 'number')
        ? ""
        : lang.handle('¡Orden de trabajo erronea!')
    );

    const found = workOrders.find((e) => e.value === value);
    setForm((prev) => ({
      ...prev,
      work_order: value,
      serial_number: prev.serial_number
        ? prev.serial_number
        : (found?.serialNumber ?? ""),
    }));
  }, [workOrders, lang, updateError]);

  const onChangeDate = useCallback((date, dateString) => {
    updateForm('date', dateString);
  }, [updateForm]);

  const navigateByState = useCallback((state) => {
    const urlMap = {
      "Fabricado": "/boards/manufacture/list/2",
      "Asignado": "/boards/manufacture/assigned/2",
      "Terminado": "/boards/list/2",
      "Eliminado": "/boards/list/2",
    };

    const targetUrl = urlMap[state] || "/boards/list/2";
    navigate(targetUrl);
  }, [navigate]);

  const onSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (loading) {
      return;
    }

    if (!isFormValid()) {
      notification.error({
        message: lang.handle('Rellene el formulario correctamente'),
      });
      return;
    }

    try {
      setLoading(true);

      const res = await service.put(`kiwi/${form.uuid}`, form);

      if (!res.data.ok) {
        notification.error({
          message: lang.handle(res.data.message),
        });
        return;
      }

      notification.success({
        message: lang.handle('Placa actualizada correctamente'),
      });

      navigateByState(form.state);
    } catch (err) {
      console.error("Error updating kiwi:", err);
      notification.error({
        message: lang.handle('Error en la operación'),
      });
    } finally {
      setLoading(false);
    }
  }, [form, loading, lang, isFormValid, navigateByState]);

  // ===== 6. EFFECTS =====
  useEffect(() => {
    fetchKiwiData();
    fetchWorkOrders();
  }, [fetchKiwiData, fetchWorkOrders]);

  // ===== 7. RENDER =====

  const title = `${lang.handle('Información de kiwi')} ${form.ssid}`;
  const kiwiVersions = versions.filter(v => v.device_type === 'kiwi');

  return (
    <div className="card">
      {loading && <Loading loading={true} />}

      {/* Título de la tarjeta */}
      <h4 className="card-title p-3">{title}</h4>

      {/* Formulario */}
      <form onSubmit={onSubmit} className="p-3">
        {/* Número de pedido */}
        <div className="form-group row">
          <label
            htmlFor="order_id"
            className="col-sm-2 col-form-label newCard-form-text"
          >
            {lang.handle('Pedido')}
          </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="order_id"
              placeholder="20PED4596"
              onChange={onInputChange}
              name="order_id"
              value={form.order_id}
              disabled={!editable}
              required
            />
          </div>
        </div>

        {/* Número de serie */}
        <div className="form-group row">
          <label
            htmlFor="serial_number"
            className="col-sm-2 col-form-label newCard-form-text"
          >
            {lang.handle('N serie')}
          </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="serial_number"
              placeholder="202005080005"
              onChange={onInputChange}
              name="serial_number"
              value={form.serial_number}
              disabled={!editable}
            />
            {errors.serial_number && (
              <span className="boardFormError">{errors.serial_number}</span>
            )}
          </div>
        </div>

        {/* Orden de trabajo */}
        <div className="form-group row">
          <label
            htmlFor="work_order"
            className="col-sm-2 col-form-label newCard-form-text"
          >
            {lang.handle('Orden de trabajo')}
          </label>
          <div className="col-sm-10">
            <Select
              className="form-control"
              id="work_order"
              placeholder="12345678"
              value={form.work_order || undefined}
              onChange={handleChangeWorkOrder}
              onSearch={handleSearchWorkOrder}
              options={workOrders}
              showSearch
              disabled={!editable}
              filterOption={(input, option) => {
                const strOption = option.label.toString();
                return strOption.toLowerCase().indexOf(input.toLowerCase()) >= 0;
              }}
            />
            {errors.work_order && (
              <span className="boardFormError">{errors.work_order}</span>
            )}
          </div>
        </div>

        {/* SSID placa */}
        <div className="form-group row">
          <label
            htmlFor="ssid"
            className="col-sm-2 col-form-label newCard-form-text"
          >
            SSID
          </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="ssid"
              placeholder="KIWI_54A6BC"
              onChange={onInputChange}
              name="ssid"
              value={form.ssid}
              disabled={!editable}
              required
            />
            {errors.ssid && (
              <span className="boardFormError">{errors.ssid}</span>
            )}
          </div>
        </div>

        {/* MAC */}
        <div className="form-group row">
          <label
            htmlFor="mac"
            className="col-sm-2 col-form-label newCard-form-text"
          >
            MAC
          </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="mac"
              placeholder="30:A4:F5:20:4C:5A"
              onChange={onInputChange}
              name="mac"
              value={form.mac}
              disabled={!editable}
            />
            {errors.mac && (
              <span className="boardFormError">{errors.mac}</span>
            )}
          </div>
        </div>

        {/* UUID placa */}
        <div className="form-group row">
          <label className="col-sm-2 col-form-label newCard-form-text">
            UUID
          </label>
          <div className="col-sm-10">
            <Select
              showSearch
              className="kiwi-form__select"
              placeholder="37555f8a-1cab-486b-ab2e-1c65e80b2c12"
              optionFilterProp="children"
              value={form.board_uuid || undefined}
              onSelect={(value) => { updateForm('board_uuid', value); }}
              filterOption={(input, option) =>
                String(option?.children)
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              filterSort={(optionA, optionB) =>
                String(optionA?.children)
                  .toLowerCase()
                  .localeCompare(String(optionB?.children).toLowerCase())
              }
              dropdownRender={(menu) => (
                <div>
                  {menu}
                  <Divider style={{ margin: "4px 0" }} />
                </div>
              )}
            >
              {boardItems.map((item) => (
                <Option key={item} value={item}>
                  {item}
                </Option>
              ))}
            </Select>
          </div>
        </div>

        {/* Fecha fabricación */}
        <div className="form-group row">
          <label className="col-sm-2 col-form-label newCard-form-text">
            {lang.handle('Fecha fabricación')}
          </label>
          <div className="col-sm-10">
            <DatePicker
              className="form-control"
              value={form.date && form.date.trim() !== "" 
                ? dayjs(form.date, "DD-MM-YYYY") 
                : null
              }
              onChange={onChangeDate}
              format="DD-MM-YYYY"
              disabled={!editable}
              required
            />
          </div>
        </div>

        {/* Versión */}
        <div className="form-group row">
          <label
            htmlFor="version_uuid"
            className="col-sm-2 col-form-label newCard-form-text"
          >
            {lang.handle('Versión')}
          </label>
          <div className="col-sm-10">
            <select
              className="form-control"
              id="version_uuid"
              onChange={onInputChange}
              name="version_uuid"
              value={form.version_uuid}
              disabled={!editable}
              required
            >
              <option value="">--{lang.handle('Selecciona una opción')}--</option>
              {kiwiVersions.map((version) => (
                <option key={version.uuid} value={version.uuid}>
                  {version.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Notas */}
        <div className="form-group row">
          <label
            htmlFor="notes"
            className="col-sm-2 col-form-label newCard-form-text"
          >
            {lang.handle('Notas')}
          </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="notes"
              placeholder={lang.handle('Este pedido debe salir con una pegatina extra')}
              onChange={onInputChange}
              name="notes"
              value={form.notes}
              disabled={!editable}
            />
          </div>
        </div>

        {/* Estado de fabricación */}
        <div className="form-group row">
          <label className="col-sm-2 col-form-label newCard-form-text">
            {lang.handle('Estado de fabricación')}
          </label>
          <div className="col-sm-10">
            <select
              className="form-control"
              onChange={onInputChange}
              name="state"
              value={form.state}
              disabled={!editable}
              required
            >
              <option value="Fabricado">{lang.handle('Fabricado')}</option>
              <option value="Asignado">{lang.handle('Asignado')}</option>
              <option value="Terminado">{lang.handle('Terminado')}</option>
              <option value="Eliminado">{lang.handle('Eliminado')}</option>
            </select>
          </div>
        </div>

        {/* Botón de guardado */}
        {editable && (
          <button
            id="button-save"
            className="btn btn-primary d-flex float-right"
            type="submit"
            disabled={loading}
          >
            <div className="btnText">
              <p>{lang.handle('Guardar')}&nbsp;&nbsp;</p>
              <i className="material-icons">save</i>
            </div>
          </button>
        )}
      </form>
    </div>
  );
}

export default withLocale(withNavigation(KiwiCard));
